// Friendly field labels for AccountCampaign audit-timeline rendering.
// Keyed by the storage field name (i.e. what shows up in auditLog
// entries' `changes[].field`). The labelForField fallback title-cases
// unknown keys — useful in dev when a new field ships before its label
// does, and defends against never getting a raw camelCase into a
// timeline entry the AM has to read.
//
// Parallel maps for other entity types (Account, Campaign, ...) land in
// P4 when those entity timelines get surfaced. Each map lives next to
// the module that owns the entity.

export const accountCampaignFieldLabels: Record<string, string> = {
  // Identity / lifecycle
  primary: 'Primary row',
  campaignId: 'Campaign',
  rowType: 'Row type',
  campaignSnapshotAt: 'Content snapshot time',
  assignedAmId: 'Account manager',
  // Round-1 polish Fix 6: per-row builder labels.
  metaBuilderId: 'Meta builder',
  googleBuilderId: 'Google builder',
  startDate: 'Start date',
  endDate: 'End date',

  // Budget
  metaMonthlyBudget: 'Meta Monthly Budget',
  metaCampaignIncrease: 'Meta Campaign Increase',
  metaMonthlyBudgetOriginal: 'Meta budget (creation-time snapshot)',
  googleMonthlyBudget: 'Google Monthly Budget',
  googleCampaignIncrease: 'Google Campaign Increase',
  googleMonthlyBudgetOriginal: 'Google budget (creation-time snapshot)',

  // Ready / execution
  metaReadyToBuild: 'Meta ready to build',
  googleReadyToBuild: 'Google ready to build',
  metaBuilt: 'Meta built',
  metaLocalBuilt: 'Meta local built',
  // Round-3 polish Fix 3: display label flips to match the new
  // Build View / grid checkbox text. Storage field stays metaTurnedOn.
  metaTurnedOn: 'Meta scheduled/live',
  metaPostRolloutCheck: 'Meta post-rollout check',
  googleBuilt: 'Google built',
  googleScheduled: 'Google scheduled',
  // Round-3 polish Fix 3: display label flips to match the new
  // Build View / grid checkbox text. Storage field stays
  // googlePostRolloutCheck.
  googlePostRolloutCheck: 'Google budget updated',

  // Content (matches the diff-set keys from diffCampaignVsRow)
  offerMechanicsUrl: 'Offer mechanics URL',
  kyckCampaignOverviewUrl: 'Kyck campaign overview URL',
  landingPageType: 'Landing page type',
  customLandingPageUrl: 'Custom landing URL',
  metaAdCopy: 'Meta ad copy',
  googleAssets: 'Google assets',
  metaLandingPageOverride: 'Meta landing override',
  googleLandingPageOverride: 'Google landing override',
  utmConfig: 'UTM config',
  metaAssetsFolderUrl: 'Meta assets folder',
  googleAssetsFolderUrl: 'Google assets folder',
  otherAssetsFolderUrl: 'Other assets folder',
  metaAssetLabels: 'Meta asset labels',
  // Granular Refresh §6.3: now compared by diffCampaignVsRow (was a
  // silent gap) and refreshable, so audit + badge surfaces need a label.
  metaCreativeFolderName: 'Meta creative folder name',

  // Pre-P2b-3 polish round 2 (Item 1) + round 5 §2.1: shared
  // descriptor (used by Meta naming AND Google labels) + Meta-only
  // template. The descriptor's label drops the "Meta" prefix since
  // it's no longer Meta-specific.
  campaignDescriptor: 'Campaign Descriptor',
  metaCampaignNameTemplate: 'Meta Campaign Name Template',
  // Pre-P2b-3 polish round 5 §2.2: Google ad-label snapshot.
  googleAdLabelTemplate: 'Google Ad Label Template',

  // Multi Lead Form spec §1.4. Row-level audit entries surface a pair
  // of keys: leadFormId (which entry the row tracks) + leadForm (the
  // frozen snapshot's authored content). The friendly labels feed the
  // describeAuditEntry helper's per-field bullets; the higher-level
  // "Row reassigned to Lead Form: 'X'" summary uses describeLeadFormFromAudit
  // (separate helper) to render the new entry's name directly.
  leadFormId: 'Lead form selection',
  leadForm: 'Lead form snapshot',

  // Per-row Campaign name override (Pre-P2b-3 §4)
  campaignNameOverride: 'Campaign name override',

  // Per-row platform override (Pre-P2b-3 §3)
  metaEnabled: 'Run on Meta override',
  googleEnabled: 'Run on Google override',

  // Notes / misc — round-1 polish Fix 4 collapsed metaNotes /
  // googleNotes into the shared rowNotes.
  rowNotes: 'Row notes',
  localAssetsUrl: 'Local assets URL',
  // P2c Local Assets: row-authored Drive labels.
  localAssetLabels: 'Local asset labels',
  metaPreviewLinks: 'Meta preview links',
  googlePreviewLinks: 'Google preview links',
  contentLastEditedAt: 'Content last edited at',
  contentLastEditedBy: 'Content last edited by',
  updatedAt: 'Updated at',
  deletedAt: 'Deleted at',
};

// Convert camelCase or snake_case to Title Case. camelCase splits give us
// "some Future Field" (the uppercase letter already starts a "word"); the
// tokenisation step then capitalises every word so snake_case inputs read
// consistently.
function titleCase(field: string): string {
  const spaced = field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return field;
  return spaced
    .split(/\s+/)
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function labelForField(field: string): string {
  return accountCampaignFieldLabels[field] ?? titleCase(field);
}
